'use client';

import React from 'react';
import { Box } from '@mui/material';
import { getAssetUrl } from '@/utils/assetUrl';

interface CharacterRef {
  characterId: string;
  /** 표시용 이름 (대표 1개). 다국어 매칭을 위해 nameVariants에 전체 들어감. */
  name: string;
  /** 다국어 매칭용 이름 후보들 (ko/en/ja 등 모두) */
  nameVariants?: string[];
  profileImagePath?: string | null;
}

/**
 * 피드 본문 텍스트를 React 노드로 렌더.
 * - #태그 → 보라색 inline chip 스타일
 * - @멘션 → 핑크색 inline pill + 캐릭터 프로필 이미지(있으면 인라인)
 *
 * 정렬 노트:
 * - pill은 `display: inline-block + verticalAlign: middle + lineHeight: 1`로 본문 baseline에 자연스럽게 정렬.
 * - 아바타는 `0.85em` 크기 + `verticalAlign: middle` → 본문 글자보다 살짝 작아서 라인 박스를 거의 부풀리지 않음.
 * - 본문 글자가 "위로 떠 보이는" 현상은 pill의 py가 line-height를 늘려서 발생 → py: 0 + lineHeight 1로 해결.
 *
 * 매칭 노트:
 * - 본문에 들어간 @이름이 캐릭터의 어떤 locale 이름과도 매칭되도록 nameVariants 전체를 키로 등록.
 * - 대소문자 무시.
 */
export function renderFeedBody(
  text: string,
  mentionedCharacters: CharacterRef[] = [],
  primaryColor: string = '#4b00cc',
): React.ReactNode[] {
  if (!text) return [];

  // 다국어 이름 모두 → 같은 캐릭으로 매핑. 키는 소문자 정규화.
  const charByName = new Map<string, CharacterRef>();
  for (const ch of mentionedCharacters) {
    const keys = new Set<string>();
    if (ch.name) keys.add(ch.name);
    if (ch.nameVariants) for (const v of ch.nameVariants) if (v) keys.add(v);
    for (const k of keys) charByName.set(k.toLowerCase(), ch);
  }
  // 추가 안전망: 멘션된 캐릭터가 1명뿐이면 @로 시작하는 모든 토큰이 그 캐릭터를 가리킬 가능성이 높음.
  const soloFallback =
    mentionedCharacters.length === 1 ? mentionedCharacters[0] : null;

  const re = /(#[\p{L}\p{N}_]{1,20}|@[\p{L}\p{N}_]{1,20})/gu;
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('#')) {
      out.push(
        <Box
          key={`tag-${key++}`}
          component="span"
          sx={{
            color: primaryColor,
            fontWeight: 700,
            bgcolor: 'rgba(75, 0, 204, 0.06)',
            borderRadius: '4px',
            // py를 따로 주지 않아 본문 line-height와 충돌하지 않음
            px: '4px',
          }}
        >
          {token}
        </Box>,
      );
    } else {
      const name = token.slice(1);
      const ch = charByName.get(name.toLowerCase()) ?? soloFallback;
      const hasAvatar = !!ch?.profileImagePath;
      out.push(
        <Box
          key={`men-${key++}`}
          component="span"
          sx={{
            display: 'inline-block',
            verticalAlign: 'middle',
            color: '#d63aa3',
            fontWeight: 700,
            fontSize: '0.92em',
            bgcolor: 'rgba(255, 110, 199, 0.12)',
            borderRadius: '999px',
            // 모든 padding을 작게 → 라인 박스가 본문보다 커지지 않음
            padding: hasAvatar ? '1px 8px 1px 3px' : '1px 8px',
            margin: '0 1px',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {hasAvatar && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '1em',
                height: '1em',
                verticalAlign: 'middle',
                marginRight: '4px',
                marginBottom: '0.05em',
                borderRadius: '50%',
                backgroundImage: `url(${getAssetUrl(ch!.profileImagePath!, 'thumb')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                bgcolor: 'rgba(0,0,0,0.06)',
              }}
            />
          )}
          <Box component="span" sx={{ verticalAlign: 'middle' }}>{token}</Box>
        </Box>,
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
